/* eslint-disable react-hooks/exhaustive-deps */
import "./BreederReport.css";

import {
  ANALYTICS_EVENTS,
  AUC_COLOR,
  DEFAULT_TRAIT_STYLE,
} from "../../constants";
import { compareAsc, differenceInDays, isWithinInterval } from "date-fns";
import { setAucData, setSelectedTrait } from "../../actions/resultMap";
import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";

import { Button } from "reactstrap";
import { ControlSection } from "../components/ControlSection/ControlSection";
import { EvolutionChart } from "../components/charts/EvolutionChart";
import { HELP_TOOLTIPS } from "../../helpTooltips/helpTooltips";
import { HelpTooltip } from "../components/HelpTooltip/HelpTooltip";
import { MultipleChart } from "../components/charts/MultipleChart";
import { OffCanvasWrapper } from "../components/advancedFiltering/OffCanvasWrapper";
import PropTypes from "prop-types";
import { PropertyName } from "../../components/PropertyName/PropertyName";
import ReactSwitch from "react-switch";
import { TraitSelector } from "../components/traitSelector/traitSelector";
import { TrialDateSelector } from "../../components/TrialDateSelector/TrialDateSelector";
import { TrialPreferences } from "../../WorldMap/MapLayerControl/components/TrialPreferences/TrialPreferences";
import { VarietyExploringRow } from "../components/VarietyExploringRow/VarietyExploringRow";
import moment from "moment";
import { selectFilteredFeatures } from "../../selectors/resultMap";
import { useKeyboardShortcut } from "../../hooks/useKeyboardShortcut";
import { useTracking } from "../../analytics";

// Folding section of the drawer. The body stays mounted and is shown or hidden
// at once: a Collapse slide ran while the other section vanished, the panel
// jumped twice. Mounted, the trait and colour searches survive a fold.
const DrawerSection = ({ icon, title, isOpen, onToggle, children }) => (
  <div className={`breeder-drawer-section ${isOpen ? "open" : ""}`}>
    <button
      type="button"
      className="breeder-drawer-section-head"
      onClick={onToggle}
    >
      <i className={`fa-solid ${icon}`} /> {title}
      <i
        className={`fa-solid ms-auto ${
          isOpen ? "fa-chevron-up" : "fa-chevron-down"
        }`}
      />
    </button>
    <div className={isOpen ? "breeder-drawer-section-body" : "d-none"}>
      {children}
    </div>
  </div>
);

DrawerSection.propTypes = {
  icon: PropTypes.string.isRequired,
  title: PropTypes.string.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  children: PropTypes.node,
};

/*
 * Breeder report template:
 * Template: implementation of dashboard for a specific need. Defines behaviours and positioning of components
 * Breeder template is a powerful implementation to rank genotypes based on their measured traits for each date, with the
 * objective of facilitating process of taking decisions for breeders
 * exclude microplots too far away from normal distribution (ground effect, etc.)
 * analyze evolution of carefully selected genotypes, compare them overtime
 * display performance on every calculated trait
 * export results in several formats
 */
export const BreederReport = () => {
  const dispatch = useDispatch();
  const filteredDatesRef = useRef();

  const [aucModeChecked, setAucModeChecked] = useState(false);
  const [aucDatesIndexes, setAucDatesIndexes] = useState([0, 1]);

  const user = useSelector((state) => state.user);
  const [featuresByDate, trial, selectedTrait] = useSelector(
    ({ resultMap }) => [
      resultMap.featuresByDate,
      resultMap.trial,
      resultMap.selectedTrait,
    ]
  );

  const currentContract = useMemo(() => {
    return trial != null
      ? user.contracts.find(({ id }) => id === trial?.contract_id)
      : null;
  }, [user.contracts, trial]);

  const plotRatingConfig = useMemo(
    () => currentContract?.plot_rating_config ?? [],
    [currentContract]
  );

  // Construct traits by date
  const propertiesByDate = useMemo(() => {
    const propertiesByDate = {};
    featuresByDate?.forEach((feature) => {
      const date = feature["date"];
      if (!Object.hasOwn(propertiesByDate, date))
        propertiesByDate[date] = Object.keys(feature.properties);
    });

    return Object.entries(propertiesByDate).map(([date, traits]) => ({
      date,
      traits,
    }));
  }, [featuresByDate]);

  // Keep only dates where selected trait is available.
  const filteredDates = useMemo(() => {
    const trait = (selectedTrait.baseTrait ?? selectedTrait).technical_name;

    return propertiesByDate
      .filter(({ traits }) => traits.includes(trait))
      .map(({ date }) => date)
      .sort();
  }, [selectedTrait, propertiesByDate]);
  // Reset aucDatesIndexes on `filteredDates` change
  if (filteredDates !== filteredDatesRef.current) {
    filteredDatesRef.current = filteredDates;
    setAucDatesIndexes([0, 1]);
  }

  // AUC
  const aucData = useSelector((state) => state.resultMap.aucData);
  const { trackEvent } = useTracking();

  // The AUC button can make the Evolution header taller, so its plot
  // shrinks. Plotly only re-measures on a window resize (`useResizeHandler`)
  // and overflowed downwards: one synthetic resize, as Chart.jsx does on expand.
  useEffect(() => {
    window.dispatchEvent(new Event("resize"));
  }, [aucModeChecked]);

  const onAucToggle = () => {
    setAucModeChecked(!aucModeChecked);
    trackEvent(ANALYTICS_EVENTS.AUC_MODE);
  };

  const aucDates = useMemo(
    () => [
      filteredDates[aucDatesIndexes[0]],
      filteredDates[aucDatesIndexes[1]],
    ],
    [aucDatesIndexes, filteredDates]
  );

  const computeAUC = () => {
    const aucDataCopy = JSON.parse(JSON.stringify(aucData));
    const traitLabel = `${scopeTrait.technical_name}|${moment(
      aucDates[0]
    ).format("MM-DD")} → ${moment(aucDates[1]).format("MM-DD")}`;
    if (
      !aucDataCopy.traits.find(
        ({ technical_name }) => technical_name === traitLabel
      )
    ) {
      const newTrait = {
        name: traitLabel,
        technical_name: traitLabel,
        isAddon: true,
        baseTrait: scopeTrait,
        style: DEFAULT_TRAIT_STYLE,
        traitGroup: scopeTrait.traitGroup,
        aucDates,
      };
      aucDataCopy.traits.push(newTrait);
      dispatch(setSelectedTrait(newTrait));
      setAucModeChecked(false);
    }

    const baseData = {};
    const startDate = new Date(aucDates[0]);
    const endDate = new Date(aucDates[1]);

    featuresByDate
      .toSorted((a, b) => compareAsc(new Date(a.date), new Date(b.date)))
      .forEach((feature) => {
        if (
          isWithinInterval(new Date(feature.date), {
            start: startDate,
            end: endDate,
          }) &&
          feature.properties[scopeTrait.technical_name] != null
        ) {
          if (!baseData[feature.id]) baseData[feature.id] = [];
          baseData[feature.id].push(feature);
        }
      });

    Object.entries(baseData).forEach(([id, features]) => {
      let area = 0;
      for (let i = 0; i < features.length - 1; i++)
        area +=
          0.5 *
          differenceInDays(
            new Date(features[i + 1].date),
            new Date(features[i].date)
          ) *
          (features[i].properties[scopeTrait.technical_name] +
            features[i + 1].properties[scopeTrait.technical_name]);

      if (!aucDataCopy.featureData[id]) aucDataCopy.featureData[id] = {};
      aucDataCopy.featureData[id][traitLabel] =
        area / differenceInDays(endDate, startDate);
    });

    dispatchSetAucData(aucDataCopy);
  };

  const scopeTrait = useMemo(
    () => (selectedTrait.isAddon ? selectedTrait.baseTrait : selectedTrait),
    [selectedTrait]
  );

  // FILTERS
  // Features filtered on experiments, modalities and layer
  const filteredFeatures = useSelector(selectFilteredFeatures);

  const dispatchSetAucData = (aucData) => {
    dispatch(setAucData(aucData));
  };

  const [isOffcanvasOpen, setIsOffcanvasOpen] = useState(false);

  const toggleOffcanvas = () => {
    setIsOffcanvasOpen(!isOffcanvasOpen);
    trackEvent(ANALYTICS_EVENTS.ANALYTICS_AF_BUTTON);
  };

  // Parameters live in a left drawer, like the PCA one: open on arrival only
  // when no site is picked yet, the page is empty and the picker is the point.
  const [drawerOpen, setDrawerOpen] = useState(!trial);
  useKeyboardShortcut("p", () => setDrawerOpen((open) => !open));
  // ControlSection outlives the closing for the slide only, then is dropped
  // (onTransitionEnd below): left mounted aside, it kept its 'U' upload
  // shortcut live and drew the floating upload card over the page.
  const [showControls, setShowControls] = useState(drawerOpen);
  if (drawerOpen && !showControls) setShowControls(true);
  // Traits and Field book take turns: both open, the colour list left the
  // trait list a zero-height leftover.
  const [openSection, setOpenSection] = useState("traits");
  const toggleSection = (section) =>
    setOpenSection((current) => (current === section ? null : section));
  // A page that never opens the Field book does not pay for it (same as PCA).
  const [hasOpenedFieldBook, setHasOpenedFieldBook] = useState(false);

  // AUC toggle, range and compute button on one row, in their own card above
  // Evolution, whose dates bar holds the AUC range slider.
  const aucBar = trial?.trial_dates && (
    <div className="powerdash-component breeder-auc">
      <div className="breeder-auc-toggle">
        <i className="fa-solid fa-chart-area discrete-icon" />
        AUC
        {filteredDates.length ? (
          <ReactSwitch
            onChange={onAucToggle}
            checked={aucModeChecked}
            disabled={!filteredDates.length}
            uncheckedIcon={<></>}
            className="react-switch"
            onColor={AUC_COLOR}
            height={20}
            width={40}
          />
        ) : (
          <i className="fa-solid fa-lg fa-spinner fa-spin discrete-icon" />
        )}
      </div>
      {aucModeChecked && filteredDates.length > 1 && (
        <span className="breeder-auc-range">
          {moment(aucDates[0]).format("MMM D")} →{" "}
          {moment(aucDates[1]).format("MMM D")}
        </span>
      )}
      {aucModeChecked &&
        scopeTrait &&
        featuresByDate.length > 0 &&
        (filteredDates.length < 2 ? (
          <span className="auc-btn-placeholder">
            Not enough date to compute AUC.
          </span>
        ) : (
          <Button className="auc-btn" onClick={computeAUC}>
            Compute integrative{" "}
            <PropertyName technicalName={scopeTrait.technical_name} />
          </Button>
        ))}
      <span className="breeder-auc-help">
        <HelpTooltip helpTooltip={HELP_TOOLTIPS.AUC} placement="bottom-end" />
      </span>
    </div>
  );

  return (
    <div className="breeder-page">
      <div className="breeder-drawer-handle">
        <div
          className={`tab powerdash-component d-flex flex-row align-items-center clickable gap-2 ${
            drawerOpen ? "active" : ""
          }`}
          onClick={() => setDrawerOpen((open) => !open)}
        >
          <i className="fa-solid fa-sliders" />
          <span className="tab-text">Site, traits &amp; export</span>
          <kbd>P</kbd>
        </div>
      </div>
      {drawerOpen && (
        <div
          className="breeder-drawer-backdrop"
          onClick={() => setDrawerOpen(false)}
        />
      )}
      <div
        className={`breeder-drawer ${drawerOpen ? "open" : ""}`}
        onTransitionEnd={(event) => {
          // Only the panel sliding counts: inner transitions bubble up here.
          if (event.target === event.currentTarget && !drawerOpen)
            setShowControls(false);
        }}
      >
        <div className="breeder-drawer-header">
          <span className="breeder-drawer-title">
            <i className="fa-solid fa-sliders" /> Site, traits &amp; export
          </span>
        </div>
        {showControls && <ControlSection alwaysExpanded showFilteredExport />}
        <DrawerSection
          icon="fa-list"
          title="Traits"
          isOpen={openSection === "traits"}
          onToggle={() => toggleSection("traits")}
        >
          <TraitSelector />
        </DrawerSection>
        {/* Inline rather than FieldBookCard: its own fixed drawer would be
            positioned against this transformed panel, not the screen. */}
        {trial && (
          <DrawerSection
            icon="fa-palette"
            title="Field book"
            isOpen={openSection === "fieldBook"}
            onToggle={() => {
              toggleSection("fieldBook");
              setHasOpenedFieldBook(true);
            }}
          >
            {hasOpenedFieldBook && <TrialPreferences />}
          </DrawerSection>
        )}
      </div>
      {drawerOpen && (
        <div
          className="breeder-drawer-close-tab p-3 d-flex align-items-center clickable"
          title="Hide parameters"
          onClick={() => setDrawerOpen(false)}
        >
          <i className="fa-solid fa-angles-left" />
        </div>
      )}

      {/* Charts side by side (tabs + ranking, dates + Evolution), varieties
          under them */}
      <div className="breeder-body">
        <div className="breeder-charts">
          <div className="breeder-tabs">
            <MultipleChart
              displayTabs={filteredFeatures.length > 0}
              selectedTrait={selectedTrait}
              plotRatingConfig={plotRatingConfig}
            />
          </div>
          <div className="breeder-evolution">
            {trial?.trial_dates && (
              // Above Evolution: the flight to load, as on the World Map, then
              // the AUC controls.
              <div className="breeder-evolution-bar">
                <div className="powerdash-component breeder-date">
                  <TrialDateSelector
                    compact
                    greyDatesWithoutResults
                    className="breeder-date-anchor"
                    popoverClassName="breeder-date-popover"
                  />
                </div>
                {aucBar}
              </div>
            )}
            <EvolutionChart
              trait={selectedTrait}
              aucModeChecked={aucModeChecked}
              aucDates={aucDates}
              // Every flight, as the old dates bar; only the trait's own once
              // the AUC range (indexes into filteredDates) is on.
              dates={aucModeChecked ? filteredDates : trial?.trial_dates ?? []}
              aucDatesIndexes={aucDatesIndexes}
              setAucDatesIndexes={setAucDatesIndexes}
              exportName={`evolution_${trial?.display_name}_${selectedTrait.name}`}
            />
          </div>
        </div>
        <div className="breeder-varieties">
          <VarietyExploringRow />
        </div>
      </div>

      <OffCanvasWrapper
        isOffcanvasOpen={isOffcanvasOpen}
        toggleOffcanvas={toggleOffcanvas}
      />
    </div>
  );
};
